import { type ComponentProps, type ForwardRefExoticComponent, type RefAttributes, useMemo, useRef } from 'react'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx'
import { ChevronDown } from 'lucide-react'

import { EditableComponent } from '@/components/EditableComponent.tsx'
import { RejectionModal } from '@/components/form/status/RejectionModal.tsx'
import { DEFAULT_STORY } from '@/constants/DEFAULT_VALUES.ts'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { useMutateStoryStatus } from '@/requests/mutations/useMutateStoryStatus.ts'
import { getPossibleStatusesByStoryType, isStartableStory } from '@/utils/story.ts'

type EditableVersionProps = {
  storyId: string
  value?: StoryStatus
  estimate: StoryPoints
  storyType: StoryType
}
export const EditableStatus = ({
  storyId,
  value = DEFAULT_STORY().status,
  estimate,
  storyType,
}: EditableVersionProps) => {
  const mutate = useMutateStoryStatus(storyId)

  const rejectionModalRef = useRef<Modal>(null)

  const isStartable = useMemo(() => {
    return isStartableStory(storyId, estimate, storyType)
  }, [storyId, estimate, storyType])

  return (
    <>
      <Select
        value={value}
        onValueChange={(newValue) => {
          if (newValue === STORY_STATUSES.REJECTED) {
            rejectionModalRef.current?.showModal()
            return
          }

          mutate(newValue as StoryStatus)
        }}
        disabled={!isStartable || value === STORY_STATUSES.UNSCHEDULED}
      >
        <SelectTrigger
          id={`story-status-${storyId}`}
          className="p-0 flex w-fit h-fit gap-1 bg-surface-neutralMedium border-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-2"
        >
          <SelectValue className="font-EN-KR-caption-regular text-contents-primary" placeholder="Story status">
            {value === STORY_STATUSES.ACCEPTED ? '' : value}
          </SelectValue>
          <ChevronDown className="h-3.5 w-3.5" />
        </SelectTrigger>
        <SelectContent align="end" alignOffset={-8} className="min-w-22 w-22 px-0 py-1 rounded-xs">
          {Object.entries(getPossibleStatusesByStoryType(storyType))
            .filter(([_, _value]) => _value !== STORY_STATUSES.UNSCHEDULED)
            .filter(([_, _value]) => _value !== value)
            .map(([_key, _value]) => (
              <SelectItem
                key={_key}
                value={_value}
                className="w-22 py-1.5 px-2 cursor-pointer relative hover:after:bg-stateLayer-onLight-hovered hover:after:opacity-8 after:absolute after:inset-0 rounded-none"
              >
                <span className="font-EN-KR-caption-regular text-contents-primary">{_value}</span>
              </SelectItem>
            ))}
        </SelectContent>
      </Select>

      <RejectionModal ref={rejectionModalRef} storyId={storyId} />
    </>
  )
}

export const TypedEditableStatus = EditableComponent as unknown as ForwardRefExoticComponent<
  EditableProps<string, undefined, ComponentProps<typeof EditableStatus>> & RefAttributes<EditableCommands>
>
